import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import LendingOperationsApi from "../../../../../../infrastructure/api/lendingOperations";
import {
  getEntityId,
  parseApiErrorMessage,
} from "../../../../../components/request/case-tabs/mehrsanOperationsUtils";

export const normalizeWorkflowApiList = (res) => {
  if (!res || res.isSuccess === false) return [];
  const data = res?.data ?? res?.Data;
  return Array.isArray(data) ? data : [];
};

/** First valid active loan process id (not deleted); no fixed-index assumption. */
export const resolveActiveLoanProcessId = (processes) => {
  if (!Array.isArray(processes)) return null;
  const match = processes.find((process) => {
    const id = Number(getEntityId(process));
    if (!Number.isFinite(id) || id <= 0) return false;
    if (process?.isDeleted === true || process?.IsDeleted === true) return false;
    if (process?.deleteOn != null || process?.DeleteOn != null) return false;
    return true;
  });
  return match ? Number(getEntityId(match)) : null;
};

export const pickActionFields = (action) => ({
  id: getEntityId(action),
  title: action?.title ?? action?.Title ?? "اقدام",
  actionType: action?.actionType ?? action?.ActionType ?? "—",
  isCompleted: Boolean(action?.isCompleted ?? action?.IsCompleted),
  code: action?.code ?? action?.Code ?? "",
});

const WorkflowActionsCard = ({ source, loanProcessId, canEdit = false }) => {
  const [loading, setLoading] = useState(false);
  const [completingId, setCompletingId] = useState(null);
  const [actions, setActions] = useState([]);

  const loadActions = useCallback(async () => {
    if (!loanProcessId || Number(loanProcessId) <= 0) {
      setActions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await LendingOperationsApi.getWorkflowActions(
        source,
        loanProcessId
      );
      setActions(normalizeWorkflowApiList(res));
    } catch {
      setActions([]);
    } finally {
      setLoading(false);
    }
  }, [loanProcessId, source]);

  useEffect(() => {
    loadActions();
  }, [loadActions]);

  const handleComplete = async (action) => {
    const fields = pickActionFields(action);
    if (!fields.id || fields.isCompleted || !canEdit) return;

    setCompletingId(fields.id);
    try {
      const res = await LendingOperationsApi.completeWorkflowAction(
        source,
        loanProcessId,
        fields.id,
        { MetadataJson: null }
      );
      if (res?.isSuccess === false) {
        throw new Error(parseApiErrorMessage(res, "ثبت اقدام ناموفق بود."));
      }
      const data = res?.data ?? res?.Data;
      const externalRef = data?.externalReference ?? data?.ExternalReference;
      const message = data?.message ?? data?.Message;
      toast.success(
        externalRef
          ? `ثبت در ایریا انجام شد (شناسه: ${externalRef}).`
          : message || "اقدام ثبت شد."
      );
      await loadActions();
    } catch (err) {
      toast.error(err?.message || "ثبت اقدام ناموفق بود.");
    } finally {
      setCompletingId(null);
    }
  };

  if (!loanProcessId || Number(loanProcessId) <= 0) {
    return null;
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <PlaylistAddCheckOutlinedIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={700}>
              اقدامات گردش کار
            </Typography>
            {loading ? <CircularProgress size={16} /> : null}
          </Stack>

          {!loading && actions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              اقدامی برای مرحله جاری تعریف نشده است.
            </Typography>
          ) : null}

          {actions.map((action) => {
            const fields = pickActionFields(action);
            if (!fields.id) return null;
            return (
              <Box
                key={fields.id}
                sx={{
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ sm: "center" }}
                  justifyContent="space-between"
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700}>
                      {fields.title}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={fields.actionType} variant="outlined" />
                      <Chip
                        size="small"
                        icon={
                          fields.isCompleted ? (
                            <CheckCircleOutlineIcon />
                          ) : undefined
                        }
                        color={fields.isCompleted ? "success" : "default"}
                        label={fields.isCompleted ? "تکمیل‌شده" : "در انتظار"}
                      />
                    </Stack>
                  </Box>
                  {canEdit && !fields.isCompleted ? (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={completingId === fields.id}
                      onClick={() => handleComplete(action)}
                    >
                      {completingId === fields.id ? "در حال ثبت..." : "تکمیل"}
                    </Button>
                  ) : null}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
};

WorkflowActionsCard.propTypes = {
  source: PropTypes.string.isRequired,
  loanProcessId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  canEdit: PropTypes.bool,
};

export default WorkflowActionsCard;
